import {
    is_likely_matching_boolean_falsy_string,
    parse_boolean_falsy_types
} from '../helpers/index';

/**
 * Determines if a URL param value should be treated as an array.
 * @param {any} value
 * @returns {boolean}
 */
export function is_array_like_url_param_value(value) {
    if (Array.isArray(value)) return true;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return (
            trimmed.includes(',') ||
            (trimmed.startsWith('[') && trimmed.endsWith(']'))
        );
    }
    return false;
}

/**
 * Parses url parameter into common types we use
 * @param {} the_value - The value to parse.
 * @returns {string|int|float|boolean|null} - The parsed value.
*/
export function detect_parse_url_param_types(the_value) {
    if (the_value === null || the_value === undefined) return null;
    if (typeof the_value !== 'string') {
        the_value = String(the_value);
    }
    if (is_likely_matching_boolean_falsy_string(the_value)) {
        return parse_boolean_falsy_types(the_value);
    } else if (!isNaN(the_value) && the_value.trim() !== '') {
        const numValue = the_value.includes('.') ? parseFloat(the_value) : parseInt(the_value, 10);
        return isNaN(numValue) ? the_value : numValue;
    } else if (typeof the_value === 'string') {
        return the_value.trim();
    }
    return null;
}

/**
 * Extracts and converts URL parameters into a data_hub_instance object.
    * @param {error_function} error_function - The error function to call if an error occurs.
    * @returns {URLSearchParams} - The URLSearchParams object.
 */
export const get_most_parent_url_params = (error_function = window?.data_hub?.error_function) => {
    let currentWindow = window;
    const urlParams = new URLSearchParams(currentWindow.location.search);

    try {
        // Keeps looping up the windows, overwriting existing URL parameters with parent ones.
        while (currentWindow !== currentWindow.parent) {
            currentWindow = currentWindow.parent;
            const parentUrlParams = new URLSearchParams(currentWindow.location.search);
            parentUrlParams.forEach((value, key) => {
                if (!urlParams.has(key)) {
                    urlParams.set(key, value);
                }
            });
        }
    } catch (error) {
        if (error instanceof DOMException && error.name === 'SecurityError') {
            console.warn('Cross-origin access blocked. Cannot read URL parameters from the parent frame.');
        } else if (error_function) {
            error_function?.("Error getting parent URL params: " + error, null);
        }
    }
    return urlParams;
};

/**
 * Extracts array values from a URL parameter which can be in various formats.
 *
 * Supported examples (all will return an array of strings):
 * - "140326"                          -> ["140326"]
 * - "140326,133600"                   -> ["140326", "133600"]
 * - "[140326]"                        -> ["140326"]
 * - "[140326,133600]"                 -> ["140326", "133600"]
 * - '["140326"]'                      -> ["140326"]
 * - '["140326","133600"]'             -> ["140326", "133600"]
 *
 * @param {string|Array|null} paramValue - The parameter value to extract.
 * @param {boolean} [single_values_to_array=true] - If true, single values are wrapped in an array.
 * @returns {Array<string>} - An array of trimmed string values.
 */
export const extract_array_values_from_url_parameter = (paramValue = null, single_values_to_array = true) => {
    if (!paramValue) {
        console.warn('extract_array_values_from_url_parameter: No parameter value provided.');
        return [];
    }

    if (Array.isArray(paramValue)) {
        // allow any types simply return 
        return paramValue;
        //return paramValue.map(val => String(val).trim());
    }

    if (typeof paramValue === 'string') {
        let processedString = paramValue.trim();

        // If it looks like a JSON-style array, try JSON.parse first so we support
        // values like ["140326","133600"] and [140326,133600].
        if (processedString.startsWith('[') && processedString.endsWith(']')) {
            try {
                const parsedJson = JSON.parse(processedString);
                if (Array.isArray(parsedJson)) {
                    return parsedJson
                        .map(val => String(val).trim())
                        .filter(val => val !== '');
                }
            } catch (e) {
                // Fall through to manual parsing below if JSON.parse fails
            }
        }

        // Remove surrounding brackets if present (non‑JSON array-like value)
        if (processedString.startsWith('[') && processedString.endsWith(']')) {
            processedString = processedString.slice(1, -1).trim();
        }

        // Helper to clean individual items (strip surrounding quotes and trim)
        const cleanItem = (val) => {
            if (typeof val !== 'string') {
                return String(val).trim();
            }
            let cleaned = val.trim();
            // Remove a single pair of matching quotes around the value
            if (
                (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
                (cleaned.startsWith("'") && cleaned.endsWith("'"))
            ) {
                cleaned = cleaned.slice(1, -1).trim();
            }
            return cleaned;
        };

        // Split by commas if present
        if (processedString.includes(',')) {
            return processedString
                .split(',')
                .map(cleanItem)
                .filter(val => val !== '');
        } else if (processedString !== '') {
            if (single_values_to_array === true) {
                return [cleanItem(processedString)];
            }
        }

    }

    console.error('extract_array_values_from_url_parameter: Unsupported parameter type:', paramValue, typeof paramValue);
    return [];
};

/**
 * Cleans and formats ActBlue refcodes from URL parameters.
 * Ensures camelCase format and removes unwanted characters.
 *
 * @param {Object} url_params - URLSearchParams object
 * @returns {Object} - Cleaned refcodes as an object
 */
export const extract_actblue_refcodes = (url_params) => {
    let refcodes = {};

    for (const [keyRaw, valueRaw] of url_params) {
        if (!keyRaw || !valueRaw) continue;

        try {
            let key = decodeURIComponent(keyRaw).trim();
            let value = decodeURIComponent(valueRaw).trim();

            if (key.toLowerCase().startsWith('refcode')) {
                let formattedKey = key.replace(/^refcode[_-]?/i, '');
                formattedKey = formattedKey.replace(/[^a-zA-Z0-9_-]/g, '');
                formattedKey = formattedKey.replace(/[-_]([a-z0-9])/gi, (_, p1) => p1.toUpperCase());

                if (formattedKey.length === 0) continue;

                formattedKey = formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1);
                refcodes[`refcode${formattedKey}`] = value;
            }
        } catch (e) {
            console.warn(`Error processing refcode: ${keyRaw}`, e);
        }
    }

    return refcodes;
};

/**
 * Converts refcodes object into URL query parameters.
 *
 * @param {Object} refcodes - Refcodes object
 * @returns {String} - Query string for ActBlue URLs
 */
export const generate_refcode_url_params = (refcodes = {}) => {
    if (!refcodes || typeof refcodes !== 'object') return '';

    const queryString = Object.entries(refcodes)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

    // Remove any trailing '&' if present
    return queryString.endsWith('&') ? queryString.slice(0, -1) : queryString;
};

/**
 * Extracts and converts metadata URL parameters. Adds them to `data_hub_instance.page_metadata`.
 *
 * Two URL forms are supported, each with **different type behavior** — pay attention because
 * this is one of the front-end "metadata lanes" that feeds Iterable `dataFields` and Iterable
 * locks each field's type on first write:
 *   - `?metadata_b64=<base64-of-JSON-object>` → `JSON.parse(atob(value))`, **types preserved
 *     verbatim** from the publisher's JSON (boolean stays boolean, number stays number, etc.).
 *   - `?metadata_<key>=<value>` (or `?metadata-<key>=<value>`) → run through
 *     {@link detect_parse_url_param_types} → bool / int / float / string sniffing.
 *
 * The simple `key:value` dataset path ({@link parse_simple_key_value_string}) intentionally
 * uses the same sniffing rules, so values arriving via URL params or a loose dataset attribute
 * converge on the same types. The base64-JSON lanes (this function and
 * {@link parse_b64_or_array_like_data_attribute}) are the explicit-types lanes for publishers
 * who need to override sniffing (e.g. force a version string `"5.0.1"` to stay a string).
 *
 * @param {URLSearchParams} url_params
 * @param {Object} data_hub_instance
 * @returns {void}
 */
export const get_metadata_url_params = (url_params = get_most_parent_url_params(), data_hub_instance = window?.data_hub) => {
    if (!data_hub_instance) return;

    if (typeof data_hub_instance.page_metadata !== 'object') {
        data_hub_instance.page_metadata = {};
    }

    for (const [keyRaw, valueRaw] of url_params) {
        try {
            if (!keyRaw || !valueRaw) continue;
            const key = decodeURIComponent(keyRaw).trim();
            const value = decodeURIComponent(valueRaw).trim();

            if (typeof key !== 'string') continue;

            if (key.toLowerCase().startsWith('metadata_b64')) {
                try {
                    const parsed = JSON.parse(atob(value));
                    Object.entries(parsed).forEach(([innerKey, innerValue]) => {
                        const cleanKey = innerKey.replace(/[^a-zA-Z0-9_-]/g, '')
                            .replace(/[-_]([a-z0-9])/gi, (_, p1) => p1.toUpperCase())
                            .replace(/^./, c => c.toLowerCase());
                        if (!data_hub_instance.page_metadata[cleanKey]) {
                            data_hub_instance.page_metadata[cleanKey] = innerValue;
                        } else {
                            console.warn('Duplicate metadata_b64 key already assigned:', cleanKey, innerValue);
                        }
                    });
                } catch (e) {
                    data_hub_instance?.error_function?.('Metadata_b64 parse error', e);
                }
            } else if (/^metadata[_\-]?/i.test(key)) {
                let cleanKey = key.replace(/^metadata[_\-]?/i, '')
                    .replace(/[^a-zA-Z0-9_-]/g, '')
                    .replace(/[-_]([a-z0-9])/gi, (_, p1) => p1.toUpperCase())
                    .replace(/^./, c => c.toLowerCase());

                    if (!data_hub_instance.page_metadata[cleanKey]) {
                        data_hub_instance.page_metadata[cleanKey] = detect_parse_url_param_types(value);
                    } else {
                        console.warn('Duplicate metadata key already assigned:', cleanKey, value);
                    }
            }
        } catch (error) {
            console.warn('Error parsing metadata param:', keyRaw, error);
        }
    }
};